import type { AdminV2EntitlementRecord, JsonValue } from "@/types/adminV2";

/**
 * Helpers for Admin V2 Entitlements UI.
 *
 * @remarks
 * Like feature flags, entitlements can be global (no organisationId) or org-scoped.
 * The UI must preserve that distinction by omitting optional fields when empty.
 *
 * @see services/api/src/service/adminV2/revenueAccessService.ts
 */
export type EntitlementSource = "admin" | "system";

export interface EntitlementsUpsertDraft {
  organisationId: string;
  entitlementKey: string;
  isEnabled: boolean;
  source: EntitlementSource;
  startsAt: string;
  expiresAt: string;
  valueRaw: string;
}

export interface EntitlementUpsertPayload {
  organisationId?: string;
  entitlementKey: string;
  isEnabled: boolean;
  source: EntitlementSource;
  value: JsonValue | null;
  startsAt?: string;
  expiresAt?: string;
}

/**
 * Normalizes a UI-provided source value into a safe entitlement source.
 *
 * @remarks
 * This guards against stale select values (or arbitrary user input) so we never
 * send unknown sources to the backend.
 */
export const toEntitlementSource = (
  value: string,
  fallback: EntitlementSource,
): EntitlementSource =>
  value === "admin" || value === "system" ? value : fallback;

export const hasScopedEntitlementsFilter = (
  organisationIdFilter: string,
): boolean => organisationIdFilter.trim().length > 0;

export const buildEntitlementUpsertPayload = (args: {
  draft: EntitlementsUpsertDraft;
  parsedValue: JsonValue | null;
}): EntitlementUpsertPayload => ({
  // Omit empty strings so the backend sees "field not provided" rather than `""`.
  ...(args.draft.organisationId.trim().length > 0
    ? { organisationId: args.draft.organisationId.trim() }
    : {}),
  entitlementKey: args.draft.entitlementKey.trim(),
  isEnabled: args.draft.isEnabled,
  source: args.draft.source,
  value: args.parsedValue,
  ...(args.draft.startsAt.trim().length > 0
    ? { startsAt: args.draft.startsAt.trim() }
    : {}),
  ...(args.draft.expiresAt.trim().length > 0
    ? { expiresAt: args.draft.expiresAt.trim() }
    : {}),
});

export const formatEntitlementSummary = (
  entitlement: AdminV2EntitlementRecord,
): string =>
  `org: ${entitlement.organisationId ?? "global"} | enabled: ${entitlement.isEnabled ? "true" : "false"} | source: ${entitlement.source}`;
